import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Sparkles, Clock, ArrowRight } from "lucide-react";

/**
 * A slim ribbon that shows the tenant's remaining trial days at the top of
 * their dashboard. Auto-hides for admin/support/vendor/customer and for
 * tenants already on a paid plan. Only renders when SaaS billing is live.
 */
export default function TrialRibbon() {
  const [state, setState] = useState({ days: null, status: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/saas/subscriptions/me");
        if (cancelled) return;
        setState({ days: data.trial_days_remaining, status: data.subscription?.status });
      } catch { /* not a tenant or endpoint 401 — silent no-op */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.status !== "trial" || state.days === null) return null;

  const urgency = state.days <= 3 ? "bg-red-50 border-red-200 text-red-800"
                : state.days <= 7 ? "bg-amber-50 border-amber-200 text-amber-800"
                : "bg-emerald-50 border-emerald-200 text-emerald-800";

  return (
    <div className={`border-b px-4 md:px-8 py-2 flex items-center justify-between gap-3 ${urgency}`} data-testid="trial-ribbon">
      <div className="flex items-center gap-2 text-xs sm:text-sm font-semibold">
        <Sparkles className="w-4 h-4 shrink-0" />
        <span>Free Trial · <b className="font-mono-tab">{state.days} day{state.days === 1 ? "" : "s"} remaining</b></span>
        <span className="hidden sm:inline text-slate-600 font-normal">— upgrade anytime to keep all your data.</span>
      </div>
      <Link to="/saas/pricing" className="text-[11px] sm:text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 hover:underline shrink-0" data-testid="ribbon-upgrade">
        Upgrade <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
